import { lazy, Suspense } from "react"

import type { Thoughts } from "@/api/models"

import { cn } from "@/lib/utils"

import { Badge } from "../../../components/ui/badge"

const LazyJsonHighlighter = lazy(() =>
  import("./lazy-json-highlighter").then((m) => ({ default: m.LazyJsonHighlighter }))
)

type ThoughtProcessProps = {
  thoughts: Thoughts[]
}

function renderDescription(description: unknown, props?: Record<string, unknown>) {
  if (Array.isArray(description)) {
    const isStringArray = description.every((item) => typeof item === "string")
    if (isStringArray) {
      return (
        <ul className="ml-2 space-y-1">
          {description.map((line, idx) => (
            <li key={idx} className="text-muted-foreground font-mono text-sm">
              {line}
            </li>
          ))}
        </ul>
      )
    }
    return (
      <Suspense
        fallback={
          <pre className="bg-sidebar text-foreground h-full max-h-[400px] overflow-y-auto rounded p-3 text-sm">
            <code>{JSON.stringify(description, null, 2)}</code>
          </pre>
        }
      >
        <LazyJsonHighlighter value={description} />
      </Suspense>
    )
  }

  return (
    <>
      <p className="italic">{JSON.stringify(description)}</p>
      <section className="flex flex-row flex-wrap items-center gap-2">
        {props &&
          Object.keys(props).map((k: string, index) => (
            <Badge key={index} className="font-medium" variant="secondary">
              {k}: {JSON.stringify(props[k])}
            </Badge>
          ))}
      </section>
    </>
  )
}

export function ThoughtProcess({ thoughts }: ThoughtProcessProps) {
  return (
    <ul className="py-2">
      {thoughts.map((thought, index) => {
        return (
          <li
            key={`${thought?.title}-${index}`}
            className={cn(
              "border-secondary relative min-h-12 border-l border-dashed pb-4 pl-5",
              "before:top-[0px] before:left-[-10px] before:border-[8px]",
              "before:border-secondary before:bg-foreground before:rounded-full before:border-solid",
              "before:absolute before:h-[20px] before:w-[20px]",
              {
                "border-none": index === thoughts.length - 1,
              }
            )}
          >
            <h3 className="font-display relative mb-px text-lg">{thought?.title}</h3>
            {renderDescription(thought?.description, thought?.props)}
          </li>
        )
      })}
    </ul>
  )
}
